import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { EmptyList } from 'components/emptyList/EmptyList'
import { Col, Row } from 'components/layout'
import { TargetFilter } from './TargetFilter'
import { Target } from './Target'
import { TargetListSearch } from './TargetListSearch'
import { TargetPayloadType } from 'storeTypes'
import { useLocation, useNavigate } from 'react-router-dom'
import { buildTargetListUrl, constants } from 'helpers'
import { VeType } from 'storeTypes/vesTypes'
import { useSearchTargetList } from 'hooks/useSearchTargetList'

import './targetList.scss'
import { useProcessTargetList } from 'hooks/useProcessTargetList'
import { useAnalytics, useBindDispatch } from 'hooks'
import { PopupModal, UpdateModal } from 'components/modal'

const EMPTY_TARGET_LIST: TargetPayloadType[] = []

type TargetListProps = {
    targetScrollRef: React.RefObject<HTMLDivElement>
    ve: VeType
    searchIcon?: JSX.Element
    searchIconPos?: 'left' | 'right'
    searchPlaceholder?: string
}

export const TargetList = ({
    targetScrollRef,
    ve,
    searchIcon,
    searchIconPos,
    searchPlaceholder
}: TargetListProps) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { pathname } = useLocation()
    const { trackEvent } = useAnalytics()

    const { filterTargetListHandler, resetTargetListFilterHandler } =
        useBindDispatch()

    useEffect(() => {
        return () => {
            resetTargetListFilterHandler(ve?.code || '')
        }
    }, [])

    const [hasUserSearched, setHasUserSearched] = React.useState(false)

    const { targetList, processedTargetList, storeSelectedFilter, isExpired } =
        useProcessTargetList({
            veCode: ve?.code,
            villageId: ve?.ve_villages?.[0]?.sf_id
        })

    const {
        sortedTargetList,
        targetListSearchValue,
        onTargetListSearchChange
    } = useSearchTargetList({
        targetList: processedTargetList || EMPTY_TARGET_LIST
    })

    const [showUpdateModal, setShowUpdateModal] = React.useState(isExpired)

    const handleUpdate = () => {
        setShowUpdateModal(false)
        window.location.reload()
    }

    const handleTargetListRedirection = (singleTarget: TargetPayloadType) => {
        const targetListRedirectUrl = buildTargetListUrl(singleTarget)

        navigate(`${targetListRedirectUrl}`, {
            state: { from: pathname }
        })
    }

    const sendSearchTargetListEvent = () => {
        trackEvent({
            category: constants.ANALYTICS.TARGET_LIST,
            action: constants.ANALYTICS.ACTIONS.TARGET_LIST_SEARCH,
            name: constants.ANALYTICS.NAMES.TARGET_LIST_SEARCH_INITIATED
        })
    }

    const handleTargetListSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target
        if (value.trim() === '') {
            setHasUserSearched(false)
        } else if (!hasUserSearched) {
            sendSearchTargetListEvent()
            setHasUserSearched(true)
        }
        onTargetListSearchChange(e)
    }

    const sendFilterTargetListEvent = (filterType: string) => {
        trackEvent({
            category: constants.ANALYTICS.TARGET_LIST,
            action: constants.ANALYTICS.ACTIONS.TARGET_LIST_FILTER,
            name: filterType
        })
    }

    const handleFilterSelection = (filterType: string) => {
        filterTargetListHandler(filterType, ve?.code || '')
        sendFilterTargetListEvent(filterType)
    }

    if (targetList.length === 0) {
        return (
            <div
                ref={targetScrollRef}
                className="target-list"
                data-testid="target-list"
            >
                <Row>
                    <Col md={12}>
                        <h2 className="target-list--header target-list--parent">
                            {t('targetList.targetListHeaderTitle')}
                        </h2>
                        <EmptyList message={t('notAvailable.targetList')} />
                    </Col>
                </Row>
            </div>
        )
    }

    if (showUpdateModal) {
        return (
            <PopupModal closeModal={() => setShowUpdateModal(false)}>
                <UpdateModal
                    onUpdate={handleUpdate}
                    message={t('targetList.updateMessage')}
                />
            </PopupModal>
        )
    }

    return (
        <div
            ref={targetScrollRef}
            className="target-list"
            data-testid="target-list"
        >
            <Row>
                <Col md={12}>
                    <h2 className="target-list--header target-list--parent">
                        {t('targetList.targetListHeaderTitle')}
                    </h2>
                    <div className="target-list--search-parent">
                        <TargetListSearch
                            onSearchInput={handleTargetListSearch}
                            searchValue={targetListSearchValue}
                            searchIcon={searchIcon}
                            searchIconPos={searchIconPos}
                            searchPlaceholder={searchPlaceholder}
                        />
                    </div>
                    <TargetFilter
                        filterHandler={handleFilterSelection}
                        veCode={ve?.code}
                    />
                    <div className="card-session target-list--card-session">
                        {sortedTargetList.length > 0 ? (
                            sortedTargetList.map((singleTarget) => (
                                <Target
                                    targetData={singleTarget}
                                    key={singleTarget.uuid}
                                    targetOnClick={() =>
                                        handleTargetListRedirection(
                                            singleTarget
                                        )
                                    }
                                    filter={storeSelectedFilter || ''}
                                    selectedFilter={storeSelectedFilter || ''}
                                    paymentSummary={singleTarget.paymentSummary}
                                />
                            ))
                        ) : (
                            <div className="target-list--empty-list">
                                <EmptyList
                                    message={t('notAvailable.targetList')}
                                />
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
        </div>
    )
}
